/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {TaxType} from '../../../helpers'
import {KTCard, KTCardBody, Select, InputPrice, Loading} from '../../../components'

import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {basicLookup} from '../../../redux/slices/lookup.slice'
import {productGetId, productUpdate} from '../../../redux/slices/product.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  ProductUnit: state.lookup.ProductUnit,
  Tax: state.lookup.Tax,
  loading: state.lookup.loading,
})

const selectProductData = (state) => ({
  isRedirect: state.product.isRedirect,
  detail: state.product.detail,
  loading: state.product.loading,
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)
  const formikRef = useRef()
  const isFormPopulatedRef = useRef(false)

  const {ProductUnit, Tax, loading} = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectProductData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(basicLookup('ProductUnit'))
    dispatch(basicLookup('Tax'))
    dispatch(productGetId(id))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(
    () =>
      Yup.object().shape({
        Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        ProductUnitId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        IsFollowStock: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        TaxId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        IsTotalStockAlarm: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      }),
    [intl]
  )

  const formValues = useMemoizedFormValues({
    Id: '',
    Name: '',
    Code: '',
    ProductUnitId: null,
    IsFollowStock: false,
    BasePurchasePrice: 0,
    BaseSalePrice: 0,
    TaxId: null,
    PurchasePriceWithTax: 0,
    SalePriceWithTax: 0,
    IsTotalStockAlarm: false,
    TotalStockAlarmCount: '',
  })

  const handleSubmit = useCallback(
    async (values, {setSubmitting}) => {
      setSubmitting(true)
      dispatch(productUpdate(values))
    },
    [dispatch]
  )

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  formikRef.current = formik

  useEffect(() => {
    if (detail && !isFormPopulatedRef.current) {
      const populatedValues = {
        Id: detail.id || '',
        Name: detail.name || '',
        Code: detail.code || '',
        ProductUnitId: detail.productUnitId || null,
        IsFollowStock: detail.isFollowStock || false,
        BasePurchasePrice: detail.basePurchasePrice || 0,
        BaseSalePrice: detail.baseSalePrice || 0,
        TaxId: detail.taxId || null,
        PurchasePriceWithTax: detail.purchasePriceWithTax || 0,
        SalePriceWithTax: detail.salePriceWithTax || 0,
        IsTotalStockAlarm: detail.isTotalStockAlarm || false,
        TotalStockAlarmCount: detail.totalStockAlarmCount || '',
      }

      formik.setValues(populatedValues)
      isFormPopulatedRef.current = true
    }
  }, [detail])

  const breadcrumbs = useMemoizedBreadcrumbs(
    () => [
      {
        title: intl.formatMessage({id: 'BC.PRODUCT_SERVICES'}),
        path: '/stock/product-services',
        isSeparator: false,
        isActive: false,
      },
      {
        title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
        path: `/stock/product-services/detail/${id}`,
        isSeparator: false,
        isActive: false,
      },
      {
        title: '',
        path: '',
        isSeparator: true,
        isActive: false,
      },
    ],
    [intl, id]
  )

  useEffect(() => {
    if (isRedirect) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
  }, [isRedirect, navigate, pageUrl, id])

  const onChangeItem = useCallback(
    (value) => {
      const tax = TaxType.find((item) => item.id == value)
      const purchasePriceWithTax =
        formik.values.BasePurchasePrice + (formik.values.BasePurchasePrice * (tax?.value || 0)) / 100
      const salePriceWithTax =
        formik.values.BaseSalePrice + (formik.values.BaseSalePrice * (tax?.value || 0)) / 100

      formik.setFieldValue(`PurchasePriceWithTax`, purchasePriceWithTax)
      formik.setFieldValue(`SalePriceWithTax`, salePriceWithTax)
      formik.setFieldValue('TaxId', Number(value))
    },
    [formik]
  )

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.PRODUCT_SERVICES.EDIT'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          {detail && (
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PRODUCT.UPDATE.NAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    required
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Name')}
                  />
                  {formik.touched.Name && formik.errors.Name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PRODUCT.UPDATE.CODE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Code')}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PRODUCT.UPDATE.PRODUCTUNIT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={ProductUnit}
                    {...formik.getFieldProps('ProductUnitId')}
                    onChange={(item) => formik.setFieldValue('ProductUnitId', Number(item?.value))}
                  />
                  {formik.touched.ProductUnitId && formik.errors.ProductUnitId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.ProductUnitId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PRODUCT.UPDATE.ISFOLLOWSTOCK'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <div className='form-check form-switch form-check-custom form-check-solid'>
                    <input
                      className='form-check-input h-30px w-50px'
                      type='checkbox'
                      checked={formik.values.IsFollowStock}
                      {...formik.getFieldProps('IsFollowStock')}
                    />
                  </div>
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PRODUCT.UPDATE.BASEPURCHASEPRICE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <InputPrice
                    {...formik.getFieldProps('BasePurchasePrice')}
                    onChange={(value) => formik.setFieldValue('BasePurchasePrice', Number(value))}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PRODUCT.UPDATE.BASESALEPRICE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <InputPrice
                    {...formik.getFieldProps('BaseSalePrice')}
                    onChange={(value) => formik.setFieldValue('BaseSalePrice', Number(value))}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'PRODUCT.UPDATE.TAX'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={Tax}
                    {...formik.getFieldProps('TaxId')}
                    onChange={(item) => onChangeItem(Number(item.value))}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PRODUCT.UPDATE.PURCHASEPRICEWITHTAX'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <InputPrice
                    {...formik.getFieldProps('PurchasePriceWithTax')}
                    onChange={(value) =>
                      formik.setFieldValue('PurchasePriceWithTax', Number(value))
                    }
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PRODUCT.UPDATE.SALEPRICEWITHTAX'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <InputPrice
                    {...formik.getFieldProps('SalePriceWithTax')}
                    onChange={(value) => formik.setFieldValue('SalePriceWithTax', Number(value))}
                  />
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PRODUCT.UPDATE.ISTOTALSTOCKALARM'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <div className='form-check form-switch form-check-custom form-check-solid'>
                    <input
                      className='form-check-input h-30px w-50px'
                      type='checkbox'
                      checked={formik.values.IsTotalStockAlarm}
                      {...formik.getFieldProps('IsTotalStockAlarm')}
                    />
                  </div>
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'PRODUCT.UPDATE.TOTALSTOCKALARMCOUNT'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('TotalStockAlarmCount')}
                  />
                </div>
              </div>
              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    className='btn btn-light'
                    onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
